<template>
  <div class="hik-video-plugin">
    <!-- 插件容器 -->
    <div :id="containerId" class="hik-plugin-host"></div>
    <pre v-if="opInfo" class="hik-video-log">{{ opInfo }}</pre>
  </div>
</template>

<script>
const HIK_PLUGIN_BASE_PATH = 'util/HKVideoPlugin3.9.9/'
const HIK_JQUERY_PATH = 'util/HKVideo/jquery-1.12.1.min.js'
const HIK_SCRIPT_MARK = 'data-nx-hik-script'

function ensureTrailingSlash(path) {
  if (!path) return '/'
  return path.endsWith('/') ? path : `${path}/`
}

function joinAssetUrl(base, path) {
  const cleanPath = String(path || '').replace(/^\/+/, '')
  if (/^https?:\/\//i.test(base)) {
    return new URL(cleanPath, ensureTrailingSlash(base)).toString()
  }
  return `${ensureTrailingSlash(base)}${cleanPath}`
}

function getJquery() {
  return window.$ || window.jQuery || window.Zepto
}

function getLoadedHikPluginBase() {
  const suffix = HIK_PLUGIN_BASE_PATH + 'webVideoCtrl.js'
  const scripts = Array.from(document.scripts || [])
  for (let i = scripts.length - 1; i >= 0; i -= 1) {
    const src = scripts[i].src || ''
    const index = src.indexOf(suffix)
    if (index > -1) {
      return src.slice(0, index + HIK_PLUGIN_BASE_PATH.length)
    }
  }
  return ''
}

function getLoadedHikAssetBase() {
  const pluginBase = getLoadedHikPluginBase()
  const index = pluginBase.indexOf(HIK_PLUGIN_BASE_PATH)
  if (index > -1) {
    return ensureTrailingSlash(pluginBase.slice(0, index))
  }
  return ''
}

function getRuntimeAssetBase() {
  if (window.__POWERED_BY_QIANKUN__ && window.__INJECTED_PUBLIC_PATH_BY_QIANKUN__) {
    return ensureTrailingSlash(window.__INJECTED_PUBLIC_PATH_BY_QIANKUN__)
  }
  const loadedBase = getLoadedHikAssetBase()
  if (loadedBase) return loadedBase
  return ensureTrailingSlash(process.env.BASE_URL || '/')
}

function loadScriptOnce(src, key) {
  const attrValue = key || src
  const existed = document.querySelector(`script[${HIK_SCRIPT_MARK}="${attrValue}"]`)
  if (existed) {
    if (existed.getAttribute('data-loaded') === 'true') return Promise.resolve()
    return new Promise((resolve, reject) => {
      existed.addEventListener('load', resolve, { once: true })
      existed.addEventListener('error', reject, { once: true })
    })
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.async = false
    script.setAttribute(HIK_SCRIPT_MARK, attrValue)
    script.onload = () => {
      script.setAttribute('data-loaded', 'true')
      resolve()
    }
    script.onerror = () => reject(new Error(`load script failed: ${src}`))
    document.head.appendChild(script)
  })
}

export default {
  name: 'HKVideo',
  props: {
    info: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      g_iWndIndex: 0,
      deviceIdentify: '',
      isLoginSuccess: false,
      channels: [],
      selectedChannel: '',
      opInfo: '',
      rtspPort: 554,
      initTimer: null,
      channelTimer: null,
      playTimer: null,
      destroyed: false,
      pluginReady: false,
      pluginInitFailed: false,
      hikBasePath: '',
      containerId: `hik-plugin-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      loginForm: {
        ip: this.info.ip,
        port: this.info.port,
        username: this.info.username,
        password: this.info.password
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.initTimer = window.setTimeout(() => {
      this.initPlugin()
    }, 2000)
  },
  activated() {
    if (this.destroyed && this.info && this.info.ip) {
      this.destroyed = false
      this.initTimer = window.setTimeout(() => {
        this.initPlugin()
      }, 200)
    }
  },
  deactivated() {
    this.teardownPlugin()
  },
  beforeDestroy() {
    this.teardownPlugin()
    window.removeEventListener('resize', this.handleResize)
  },

  methods: {
    showOPInfo(info) {
      const time = new Date().toLocaleString()
      this.opInfo = `${time} ${info}\n` + this.opInfo
      if (this.opInfo.split('\n').length > 20) {
        this.opInfo = this.opInfo.split('\n').slice(0, 20).join('\n')
      }
    },

    resolveHikBasePath() {
      const loadedBase = getLoadedHikPluginBase()
      if (loadedBase) return loadedBase
      return joinAssetUrl(getRuntimeAssetBase(), HIK_PLUGIN_BASE_PATH)
    },

    async ensureHikAssets() {
      const runtimeBase = getRuntimeAssetBase()
      const hikBasePath = this.resolveHikBasePath()
      const jqueryUrl = joinAssetUrl(runtimeBase, HIK_JQUERY_PATH)
      const webCtrlUrl = joinAssetUrl(hikBasePath, 'webVideoCtrl.js')

      if (!getJquery()) {
        await loadScriptOnce(jqueryUrl, 'jquery')
      }

      if (window.__POWERED_BY_QIANKUN__ && window.WebVideoCtrl && window.__NX_HIK_PLUGIN_BASE__ !== hikBasePath) {
        window.WebVideoCtrl = undefined
      }

      if (!window.WebVideoCtrl) {
        await loadScriptOnce(webCtrlUrl, webCtrlUrl)
      }

      window.__NX_HIK_PLUGIN_BASE__ = hikBasePath
      this.hikBasePath = hikBasePath
    },

    async initPlugin() {
      if (this.destroyed) return
      this.pluginReady = false
      this.pluginInitFailed = false
      try {
        await this.ensureHikAssets()
      } catch (error) {
        console.warn(error)
        this.pluginInitFailed = true
        this.showOPInfo('海康插件资源加载失败，请检查主应用是否已部署出入库子应用静态资源。')
        return
      }
      if (!window.WebVideoCtrl) {
        this.pluginInitFailed = true
        this.showOPInfo('未检测到海康WebVideoCtrl插件，请先安装！')
        return
      }
      try {
        if (typeof window.WebVideoCtrl.I_CheckPluginInstall === 'function' && window.WebVideoCtrl.I_CheckPluginInstall() === -1) {
          this.pluginInitFailed = true
          this.showOPInfo('未检测到海康本地插件服务，请使用页面下载的 HCWebSDKPlugin.exe 安装或修复后，重启浏览器再试。')
          return
        }
      } catch (error) {
        console.warn(error)
      }
      const jq = getJquery()
      if (!jq) {
        this.pluginInitFailed = true
        this.showOPInfo('未检测到海康插件依赖 jQuery，请检查静态资源。')
        return
      }
      const _this = this
      window.WebVideoCtrl.I_InitPlugin({
        bWndFull: true,
        iWndowType: 1,
        szBasePath: this.hikBasePath,
        cbSelWnd: (xmlDoc) => {
          _this.g_iWndIndex = parseInt(jq(xmlDoc).find("SelectWnd").eq(0).text(), 10)
        },
        cbInitPluginComplete: () => {
          if (this.destroyed) return
          window.WebVideoCtrl.I_InsertOBJECTPlugin(this.containerId).then(() => {
            if (this.destroyed) {
              this.teardownPlugin()
              return
            }
            this.pluginReady = true
            this.pluginInitFailed = false
            window.WebVideoCtrl.I_CheckPluginVersion().then((bFlag) => {
              if (bFlag) {
                this.showOPInfo('检测到新的插件版本，请升级！')
              } else {
                this.showOPInfo('插件初始化成功，版本为最新')
                this.handleResize()
                this.handleLogin()
              }
            })
          }).catch((error) => {
            this.pluginReady = false
            this.pluginInitFailed = true
            const code = error && error.errorCode ? `，错误码：${error.errorCode}` : ''
            const message = Number(error && error.errorCode) === 3000
              ? '插件初始化失败，本地海康插件服务未连通，请关闭其它摄像头预览页，使用页面下载的 HCWebSDKPlugin.exe 安装或修复后，重启浏览器再试'
              : '插件初始化失败，请关闭其它摄像头预览页后重试，并确认已安装插件'
            this.showOPInfo(`${message}${code}。`)
          })
        }
      })
    },

    handleLogin() {
      console.log('触发了登录:', this.info)
      const { ip, port, username, password } = this.info
      if (!ip || !port) {
        this.showOPInfo('IP和端口不能为空！')
        return
      }
      if (!username) {
        this.showOPInfo('账号不能为空！')
        return
      }
      this.deviceIdentify = `${ip}_${port}`
      window.WebVideoCtrl.I_Login(ip, 101, port, username, password, {
        timeout: 1000,
        success: (xmlDoc) => {
          this.showOPInfo(`${this.deviceIdentify} 登录成功！`)
          this.isLoginSuccess = true
          this.getDevicePort()
          this.channelTimer = window.setTimeout(() => {
            this.getChannelInfo()
          }, 1000)
        },
        error: (oError) => {
          this.showOPInfo(`${this.deviceIdentify} 登录失败：${oError.errorCode || '未知错误'}`)
          this.isLoginSuccess = false
        }
      })
    },

    handleLogout(silent = false) {
      if (!window.WebVideoCtrl || !this.deviceIdentify) return Promise.resolve()

      const deviceIdentify = this.deviceIdentify
      return window.WebVideoCtrl.I_Logout(deviceIdentify).then(() => {
        if (!silent) this.showOPInfo(`${deviceIdentify} 退出成功！`)
        this.isLoginSuccess = false
        this.channels = []
        this.selectedChannel = ''
        this.deviceIdentify = ''
      }).catch(() => {
        if (!silent) this.showOPInfo(`${deviceIdentify} 退出失败！`)
      })
    },

    getDevicePort() {
      window.WebVideoCtrl.I_GetDevicePort(this.deviceIdentify).then((oPort) => {
        this.rtspPort = oPort.iRtspPort || 554
        this.showOPInfo(`获取设备端口成功，RTSP端口：${this.rtspPort}`)
      }).catch((oError) => {
        this.showOPInfo(`获取设备端口失败：${oError.errorCode}`)
      })
    },

    getChannelInfo() {
      const jq = getJquery()
      if (!jq) {
        this.showOPInfo('未检测到海康插件依赖 jQuery，请检查静态资源。')
        return
      }
      this.channels = []

      window.WebVideoCtrl.I_GetAnalogChannelInfo(this.deviceIdentify, {
        success: (xmlDoc) => {
          const oChannels = jq(xmlDoc).find("VideoInputChannel")
          oChannels.each((i, item) => {
            const id = jq(item).find("id").eq(0).text()
            const name = jq(item).find("name").eq(0).text() || `Camera ${i < 9 ? '0' + (i + 1) : (i + 1)}`
            this.channels.push({ id, name })
          })
          this.showOPInfo(`获取模拟通道成功，共${oChannels.length}个`)

          if (this.channels.length > 0 && !this.selectedChannel) {
            this.selectedChannel = this.resolvePreviewChannel()
          }
        },
        error: (oError) => {
          this.showOPInfo(`获取模拟通道失败：${oError.errorCode}`)
        }
      })

      window.WebVideoCtrl.I_GetDigitalChannelInfo(this.deviceIdentify, {
        success: (xmlDoc) => {
          const oChannels = jq(xmlDoc).find("InputProxyChannelStatus")
          oChannels.each((i, item) => {
            const online = jq(item).find("online").eq(0).text()
            if (online === 'false') return

            const id = jq(item).find("id").eq(0).text()
            const name = jq(item).find("name").eq(0).text() || `IPCamera ${i < 9 ? '0' + (i + 1) : (i + 1)}`
            this.channels.push({ id, name })
          })
          this.showOPInfo(`获取数字通道成功，共${oChannels.length}个`)

          if (this.channels.length > 0 && !this.selectedChannel) {
            this.selectedChannel = this.resolvePreviewChannel()
          }
        },
        error: (oError) => {
          this.showOPInfo(`获取数字通道失败：${oError.errorCode}`)
        }
      })

      this.playTimer = window.setTimeout(() => {
        this.startRealPlay()
      }, 1000)
    },

    resolvePreviewChannel() {
      if (!this.channels.length) return ''
      const preferred = this.info.channel == null ? '' : String(this.info.channel)
      const hit = preferred ? this.channels.find((item) => String(item.id) === preferred) : null
      return hit ? hit.id : this.channels[0].id
    },

    startRealPlay() {
      if (this.destroyed || !this.pluginReady) return
      const jq = getJquery()
      if (this.channels.length) {
        this.selectedChannel = this.resolvePreviewChannel()
      }
      if (!this.deviceIdentify || !this.selectedChannel) {
        this.showOPInfo('请先登录并选择通道！')
        return
      }
      const oWndInfo = window.WebVideoCtrl.I_GetWindowStatus(this.g_iWndIndex)
      const iStreamType = this.info.streamType != null ? Number(this.info.streamType) : 1
      const bZeroChannel = false
      const iChannelID = parseInt(this.selectedChannel, 10)

      console.log('iStreamType', iStreamType)
      console.log('iChannelID', iChannelID)
      console.log('bZeroChannel', bZeroChannel)
      console.log('iPort', this.rtspPort)
      console.log('deviceIdentify', this.deviceIdentify)

      const startPlay = () => {
        try {
          window.WebVideoCtrl.I_StartRealPlay(this.deviceIdentify, {
            iStreamType,
            iChannelID,
            bZeroChannel,
            iPort: this.rtspPort,
            success: () => {
              this.showOPInfo(`通道${this.selectedChannel}开始预览成功！`)
              if (jq) jq(`#${this.containerId}`).css("opacity", "1")
            },
            error: (oError) => {
              this.showOPInfo(`通道${this.selectedChannel}开始预览失败：${oError.errorCode}`)
            }
          })
        } catch (error) {
          console.warn(error)
        }
      }

      startPlay()
      this.handleResize()
    },

    stopRealPlay(silent = false) {
      if (!window.WebVideoCtrl) return
      const oWndInfo = window.WebVideoCtrl.I_GetWindowStatus(this.g_iWndIndex)
      if (!oWndInfo) {
        if (!silent) this.showOPInfo('当前无预览视频！')
        return
      }

      window.WebVideoCtrl.I_Stop({
        success: () => {
          this.showOPInfo('停止预览成功！')
        },
        error: (oError) => {
          this.showOPInfo(`停止预览失败：${oError.errorCode}`)
        }
      })
    },

    async switchCamera() {
      if (this.destroyed || !this.info || !this.info.ip) return
      this.clearTimers()
      if (!this.pluginReady || this.pluginInitFailed || !window.WebVideoCtrl) {
        await this.initPlugin()
        return
      }
      this.stopRealPlay(true)
      await this.handleLogout(true)
      this.isLoginSuccess = false
      this.channels = []
      this.selectedChannel = ''
      this.deviceIdentify = ''
      this.rtspPort = 554
      this.handleLogin()
    },

    handleResize() {
      let scale = 1
      const jq = getJquery()
      if (!window.WebVideoCtrl || !this.pluginReady || this.pluginInitFailed || !jq) return
      const pluginHost = jq(`#${this.containerId}`)
      if (!pluginHost || !pluginHost.length) return
      const width = pluginHost.width()
      const height = pluginHost.height()
      if (!width || !height) return
      try {
        window.WebVideoCtrl.I_Resize(width * scale, height * scale)
      } catch (error) {
        console.warn('调整海康插件窗口大小失败', error)
      }
    },

    clearTimers() {
      ['initTimer', 'channelTimer', 'playTimer'].forEach((key) => {
        if (this[key]) {
          window.clearTimeout(this[key])
          this[key] = null
        }
      })
    },

    clearPluginDom() {
      const jq = getJquery()
      if (jq && jq(`#${this.containerId}`)) {
        jq(`#${this.containerId}`).html("")
        jq(`#${this.containerId}`).css("opacity", "0")
      }
      const pluginContainer = document.getElementById(this.containerId)
      if (pluginContainer) {
        pluginContainer.innerHTML = ""
      }
    },

    hidePluginWindow() {
      if (!window.WebVideoCtrl) return
      try {
        if (typeof window.WebVideoCtrl.I_HideWndPlugin === 'function') {
          window.WebVideoCtrl.I_HideWndPlugin()
        }
      } catch (e) {
        console.warn('隐藏海康插件窗口失败', e)
      }
    },

    teardownPlugin() {
      if (this.destroyed) return
      this.destroyed = true
      this.pluginReady = false
      this.pluginInitFailed = false
      this.clearTimers()
      this.hidePluginWindow()
      this.stopRealPlay()
      this.handleLogout()
      if (window.WebVideoCtrl && typeof window.WebVideoCtrl.I_DestroyPlugin === 'function') {
        try {
          window.WebVideoCtrl.I_DestroyPlugin().then(() => {
            this.clearPluginDom()
          }).catch((err) => {
            console.warn("销毁插件失败", err)
            this.clearPluginDom()
          })
        } catch (e) {
          console.warn("销毁插件异常", e)
          this.clearPluginDom()
        }
      } else {
        this.clearPluginDom()
      }
    }
  }
}
</script>

<style scoped>
.hik-video-plugin {
  padding: 2px;
  width: 100%;
  height: 100%;
  text-align: left;
  position: relative;
}

.hik-plugin-host {
  pointer-events: none;
  display: inline-block;
  width: 100%;
  height: 100%;
}

.hik-video-log {
  position: absolute;
  left: 8px;
  bottom: 8px;
  max-width: calc(100% - 16px);
  max-height: 120px;
  margin: 0;
  padding: 8px 10px;
  overflow: auto;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.58);
  color: #e5e7eb;
  font-size: 12px;
  line-height: 1.45;
  white-space: pre-wrap;
  pointer-events: none;
}
</style>
